// PDF chart drawing utilities — vertical bar, line, and donut.
// All take a jsPDF `doc` instance plus position/size + data array.
import { NAVY, COLORS, formatValue } from './utils';

export function drawVerticalBarChart(doc, x, y, w, h, data, { labelKey = 'label', valueKey = 'value', title = '', maxBars = 10, valueFormatter = null } = {}) {
  const items = data.slice(0, maxBars);
  if (items.length === 0) return y;

  // Allow callers to override how values are stringified. Default is the
  // currency formatter (formatValue -> "Rs 1.2B"); pass a custom formatter
  // for counts, percentages, days, etc. so we don't prefix Rs on non-currency axes.
  const fmt = typeof valueFormatter === 'function' ? valueFormatter : formatValue;

  const maxVal = Math.max(...items.map(d => d[valueKey] || 0), 1);
  const marginLeft = 20;
  const marginBottom = 18;
  const chartW = w - marginLeft - 5;
  const chartH = h - marginBottom - 8;
  const cx = x + marginLeft;
  const cy = y + 8;

  if (title) {
    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(title, x, y + 4);
  }

  // Background
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(cx, cy, chartW, chartH, 1, 1, 'F');

  // Grid lines (4 horizontal) + Y-axis labels
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.15);
  for (let g = 0; g <= 4; g++) {
    const gy = cy + chartH - (g / 4) * chartH;
    doc.line(cx, gy, cx + chartW, gy);
    const gridVal = (g / 4) * maxVal;
    doc.setFontSize(5.5);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(130);
    doc.text(fmt(gridVal), x, gy + 1.5);
  }

  // Bars
  const barGap = 2;
  const barW = Math.min(14, (chartW - (items.length + 1) * barGap) / items.length);
  const totalBarsW = items.length * barW + (items.length - 1) * barGap;
  const startX = cx + (chartW - totalBarsW) / 2;

  items.forEach((item, i) => {
    const val = item[valueKey] || 0;
    const barH = (val / maxVal) * chartH;
    const bx = startX + i * (barW + barGap);
    const by = cy + chartH - barH;
    const color = COLORS[i % COLORS.length];

    doc.setFillColor(...color);
    doc.roundedRect(bx, by, barW, barH, 1, 1, 'F');
    doc.setFillColor(Math.min(255, color[0] + 40), Math.min(255, color[1] + 40), Math.min(255, color[2] + 40));
    doc.rect(bx + 0.5, by + 0.5, barW * 0.3, Math.max(barH - 1, 0), 'F');

    // Value on top
    doc.setFontSize(5);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(60);
    doc.text(fmt(val), bx + barW / 2, by - 1.5, { align: 'center' });

    // X-axis label
    doc.setFontSize(5);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(80);
    const label = String(item[labelKey] || '').substring(0, 12);
    doc.text(label, bx + barW / 2, cy + chartH + 4, { align: 'center' });
  });

  return y + h + 3;
}

export function drawLineChart(doc, x, y, w, h, data, { labelKey = 'label', valueKey = 'value', title = '' } = {}) {
  const items = data.slice(0, 14);
  if (items.length < 2) return y;

  const maxVal = Math.max(...items.map(d => d[valueKey] || 0), 1);
  const marginLeft = 20;
  const marginBottom = 16;
  const chartW = w - marginLeft - 5;
  const chartH = h - marginBottom - 8;
  const cx = x + marginLeft;
  const cy = y + 8;

  if (title) {
    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(title, x, y + 4);
  }

  doc.setFillColor(248, 249, 252);
  doc.roundedRect(cx, cy, chartW, chartH, 1, 1, 'F');

  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.15);
  for (let g = 0; g <= 4; g++) {
    const gy = cy + chartH - (g / 4) * chartH;
    doc.line(cx, gy, cx + chartW, gy);
    doc.setFontSize(5.5);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(130);
    doc.text(formatValue((g / 4) * maxVal), x, gy + 1.5);
  }

  const points = items.map((item, i) => ({
    px: cx + (i / (items.length - 1)) * chartW,
    py: cy + chartH - ((item[valueKey] || 0) / maxVal) * chartH,
  }));

  // Area fill
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i], p2 = points[i + 1];
    const bottom = cy + chartH;
    doc.setFillColor(219, 234, 254);
    const stripW = (p2.px - p1.px);
    const avgH = bottom - (p1.py + p2.py) / 2;
    doc.rect(p1.px, (p1.py + p2.py) / 2, stripW, avgH, 'F');
  }

  // Line
  doc.setDrawColor(59, 130, 246);
  doc.setLineWidth(0.6);
  for (let i = 0; i < points.length - 1; i++) {
    doc.line(points[i].px, points[i].py, points[i + 1].px, points[i + 1].py);
  }

  // Data points
  points.forEach((p) => {
    doc.setFillColor(255, 255, 255);
    doc.circle(p.px, p.py, 1.2, 'FD');
    doc.setFillColor(59, 130, 246);
    doc.circle(p.px, p.py, 0.7, 'F');
  });

  // X-axis labels (every Nth)
  const step = Math.max(1, Math.floor(items.length / 6));
  items.forEach((item, i) => {
    if (i % step === 0 || i === items.length - 1) {
      doc.setFontSize(5);
      doc.setFont('Helvetica', 'normal');
      doc.setTextColor(80);
      const label = String(item[labelKey] || '').replace('2023- ', '').substring(0, 6);
      doc.text(label, points[i].px, cy + chartH + 4, { align: 'center' });
    }
  });

  return y + h + 3;
}

export function drawDonutChart(doc, cx, cy, outerR, data, { labelKey = 'label', valueKey = 'value', title = '' } = {}) {
  const total = data.reduce((s, d) => s + (d[valueKey] || 0), 0);
  if (total === 0) return;
  const items = data.slice(0, 8);
  const innerR = outerR * 0.55;

  if (title) {
    doc.setFontSize(9);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(title, cx - outerR, cy - outerR - 3);
  }

  let startAngle = -Math.PI / 2;

  items.forEach((item, i) => {
    const slice = ((item[valueKey] || 0) / total) * 2 * Math.PI;
    if (slice < 0.01) { startAngle += slice; return; }
    const color = COLORS[i % COLORS.length];
    doc.setFillColor(...color);

    const steps = Math.max(20, Math.ceil(slice / 0.05));
    for (let s = 0; s < steps; s++) {
      const a1 = startAngle + (s / steps) * slice;
      const a2 = startAngle + ((s + 1) / steps) * slice;
      const ox1 = cx + outerR * Math.cos(a1);
      const oy1 = cy + outerR * Math.sin(a1);
      const ox2 = cx + outerR * Math.cos(a2);
      const oy2 = cy + outerR * Math.sin(a2);
      const ix1 = cx + innerR * Math.cos(a1);
      const iy1 = cy + innerR * Math.sin(a1);
      const ix2 = cx + innerR * Math.cos(a2);
      const iy2 = cy + innerR * Math.sin(a2);
      doc.triangle(ox1, oy1, ox2, oy2, ix1, iy1, 'F');
      doc.triangle(ix1, iy1, ox2, oy2, ix2, iy2, 'F');
    }
    startAngle += slice;
  });

  // Center white circle (donut hole)
  doc.setFillColor(255, 255, 255);
  const holeSteps = 40;
  for (let s = 0; s < holeSteps; s++) {
    const a1 = (s / holeSteps) * 2 * Math.PI;
    const a2 = ((s + 1) / holeSteps) * 2 * Math.PI;
    doc.triangle(
      cx, cy,
      cx + innerR * Math.cos(a1), cy + innerR * Math.sin(a1),
      cx + innerR * Math.cos(a2), cy + innerR * Math.sin(a2),
      'F'
    );
  }

  doc.setFontSize(7);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(formatValue(total), cx, cy + 1, { align: 'center' });
  doc.setFontSize(4.5);
  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(120);
  doc.text('Total', cx, cy + 4.5, { align: 'center' });

  // Legend
  const legendX = cx + outerR + 10;
  let legendY = cy - (items.length * 5.5) / 2;
  items.forEach((item, i) => {
    doc.setFillColor(...COLORS[i % COLORS.length]);
    doc.roundedRect(legendX, legendY - 2, 5, 3.5, 0.5, 0.5, 'F');
    doc.setFontSize(6);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(60);
    const pct = total > 0 ? ((item[valueKey] / total) * 100).toFixed(1) : '0';
    doc.text(`${String(item[labelKey]).substring(0, 18)}`, legendX + 7, legendY + 0.5);
    doc.setFontSize(5.5);
    doc.setTextColor(120);
    doc.text(`${pct}% · ${formatValue(item[valueKey])}`, legendX + 7, legendY + 4);
    legendY += 7;
  });
}

// Backwards-compat aliases (used by some legacy call sites in the main builder)
export const drawBarChart = drawVerticalBarChart;
export const drawPieChart = drawDonutChart;
